import { GOOGLE_FONTS_FAMILY_CATALOG } from './googleFontsCatalog';

export const DEFAULT_TEXT_LAYER_FONT_FAMILY = null;

export const TEXT_LAYER_GOOGLE_FONT_FAMILIES = GOOGLE_FONTS_FAMILY_CATALOG;
export const TEXT_LAYER_FONT_SEARCH_RESULT_LIMIT = 60;

export const TEXT_LAYER_FONT_PICKER_DEFAULT_FAMILIES = [
  'Inter',
  'Montserrat',
  'Poppins',
  'Roboto',
  'Work Sans',
  'Oswald',
  'Merriweather',
  'Playfair Display',
];

const textLayerGoogleFontFamilySet = new Set(TEXT_LAYER_GOOGLE_FONT_FAMILIES);
const compareTextLayerFontFamilies = (left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' });

export const sanitizeTextLayerFontFamily = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return textLayerGoogleFontFamilySet.has(trimmed) ? trimmed : null;
};

const escapeCanvasFontFamily = (value) => (
  String(value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
);

export const buildCanvasFontString = ({ weight = '400', px = 16, family = null } = {}) => {
  const numericPx = Number(px);
  const safePx = Number.isFinite(numericPx) ? Math.max(0, numericPx) : 16;
  const safeWeight = (typeof weight === 'string' && weight.trim())
    ? weight.trim()
    : (Number.isFinite(Number(weight)) ? String(Number(weight)) : '400');
  const sanitizedFamily = sanitizeTextLayerFontFamily(family);
  if (!sanitizedFamily) {
    return `${safeWeight} ${safePx}px Arial, sans-serif`;
  }
  return `${safeWeight} ${safePx}px "${escapeCanvasFontFamily(sanitizedFamily)}", Arial, sans-serif`;
};

export const collectTextLayerFontFamilies = (shapePrimitives) => {
  const source = Array.isArray(shapePrimitives) ? shapePrimitives : [];
  const unique = new Set();
  source.forEach((shape) => {
    if (shape?.layerKind !== 'text') return;
    const family = sanitizeTextLayerFontFamily(shape?.textFontFamily);
    if (!family) return;
    unique.add(family);
  });
  return [...unique];
};

export const filterTextLayerFontFamilies = (
  query,
  sourceFamilies = TEXT_LAYER_GOOGLE_FONT_FAMILIES,
) => {
  const normalizedQuery = typeof query === 'string' ? query.trim().toLowerCase() : '';
  const families = Array.isArray(sourceFamilies) ? sourceFamilies : TEXT_LAYER_GOOGLE_FONT_FAMILIES;
  const prefixMatches = [];
  const substringMatches = [];
  if (!normalizedQuery) return [...families].sort(compareTextLayerFontFamilies);
  families.forEach((family) => {
    const normalizedFamily = family.toLowerCase();
    if (normalizedFamily.startsWith(normalizedQuery)) {
      prefixMatches.push(family);
      return;
    }
    if (normalizedFamily.includes(normalizedQuery)) {
      substringMatches.push(family);
    }
  });
  prefixMatches.sort(compareTextLayerFontFamilies);
  substringMatches.sort(compareTextLayerFontFamilies);
  return [...prefixMatches, ...substringMatches];
};

export const getTextLayerFontSearchResults = (
  query,
  {
    limit = TEXT_LAYER_FONT_SEARCH_RESULT_LIMIT,
    sourceFamilies = TEXT_LAYER_GOOGLE_FONT_FAMILIES,
  } = {},
) => {
  const matches = filterTextLayerFontFamilies(query, sourceFamilies);
  const normalizedLimit = Math.max(1, Number(limit) || TEXT_LAYER_FONT_SEARCH_RESULT_LIMIT);
  return {
    families: matches.slice(0, normalizedLimit),
    hasMore: matches.length > normalizedLimit,
    totalMatches: matches.length,
  };
};
